import type { ActionFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import invariant from 'tiny-invariant';
import type {
    CreateOrUpdateInspirationMutation,
    CreateOrUpdateInspirationMutationVariables,
} from 'generated/graphql';
import { CreateOrUpdateInspirationDocument } from 'generated/graphql';

export const CREATE_BUILD_HUB_INSPIRATION_API_ROUTE =
    '/remix-api/app/build-hub-inspiration-create';

const DEFAULT_FAILED_MESSAGE = 'Failed to create BuildHub inspiration.';

/**
 * Action to create a new inspiration group and link it to a BuildHub.
 */
export const action: ActionFunction = async ({ request, context }) => {
    const data = await request.formData();

    invariant(data.get('id'), 'No BuildHub ID submitted for update.');

    const variables: CreateOrUpdateInspirationMutationVariables = {
        BuildHubID: data.get('id') as string,
    };

    const inspirationGroupId = data.get('inspirationGroupId');
    if (inspirationGroupId) {
        variables.InspirationGroupID = inspirationGroupId as string;
    }

    const title = data.get('title');
    if (title) {
        variables.Title = title;
    }

    const description = data.get('description');
    if (description !== null && description !== undefined) {
        variables.Description = description;
    }

    // Handle access control - prefer new accessMode/accessRole over legacy isPrivate
    const accessMode = data.get('accessMode');
    const accessRole = data.get('accessRole');

    if (accessMode && accessRole) {
        // New access control approach
        // Note: TypeScript types will be updated after running pnpm codegen
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (variables as any).GeneralAccessMode = accessMode as string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (variables as any).GeneralAccessRole = accessRole as string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (variables as any).InheritPermissions = false;
    } else {
        // Legacy isPrivate support - map to access control
        const isPrivate = data.get('isPrivate');
        if (isPrivate) {
            const isPrivateBool = ['true', '1'].includes(isPrivate);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (variables as any).GeneralAccessMode = isPrivateBool
                ? 'restricted'
                : 'project';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (variables as any).GeneralAccessRole = 'Editor';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (variables as any).InheritPermissions = false;
        }
    }

    const parentId = data.get('parentId');
    if (parentId) {
        variables.ParentID = Number(parentId);
    }

    const sortOrder = data.get('sortOrder');
    if (sortOrder) {
        variables.SortOrder = Number(sortOrder);
    }

    try {
        const { data: result } = await context.client.mutate<
            CreateOrUpdateInspirationMutation,
            CreateOrUpdateInspirationMutationVariables
        >({
            mutation: CreateOrUpdateInspirationDocument,
            variables,
        });

        const validResult = result?.projectInspirationGroupUpsert;
        if (!validResult) {
            return json({
                projectInspirationGroupUpsert: {
                    __typename: 'ServerError',
                    Message: DEFAULT_FAILED_MESSAGE,
                },
            });
        }

        return json(result);
    } catch (error) {
        return json({
            projectInspirationGroupUpsert: {
                __typename: 'ServerError',
                Message:
                    error instanceof Error
                        ? error.message
                        : DEFAULT_FAILED_MESSAGE,
            },
        });
    }
};
